import { remove, createElement, extend } from '@syncfusion/ej2-base';
import { Dialog } from '@syncfusion/ej2-popups';
import { QueryBuilder } from '@syncfusion/ej2-querybuilder';
import { Query } from '@syncfusion/ej2-data';
import * as events from '../base/constant';
var AdvancedFilter = /** @class */ (function () {
    function AdvancedFilter(parent, serviceLocator) {
        this.dialog = null;
        this.queryBuilder = null;
        this.currentRule = null;
        this.filterRuleToRestore = null;
        this.qbContainer = null;
        this.dialogContainer = null;
        /** @hidden */
        this.baseQuery = null;
        this.hasEmptyState = false;
        this.isAdvancedFilterApplied = false;
        this.filterRequestType = null;
        this.parent = parent;
        this.serviceLocator = serviceLocator;
        this.l10n = this.serviceLocator.getService('localization');
        this.addEventListener();
    }
    AdvancedFilter.prototype.getModuleName = function () {
        return 'advancedFilter';
    };
    AdvancedFilter.prototype.destroy = function () {
        this.removeEventListener();
        if (this.queryBuilder && !this.queryBuilder.isDestroyed) {
            this.queryBuilder.destroy();
        }
        if (this.dialog && !this.dialog.isDestroyed) {
            this.dialog.destroy();
        }
        if (this.dialogContainer) {
            remove(this.dialogContainer);
            this.dialogContainer = null;
        }
        this.queryBuilder = null;
        this.dialog = null;
        this.currentRule = null;
        this.filterRuleToRestore = null;
        this.baseQuery = null;
        this.hasEmptyState = false;
        this.filterRequestType = null;
    };
    AdvancedFilter.prototype.addEventListener = function () {
        if (this.parent.isDestroyed) {
            return;
        }
        this.parent.on(events.refreshComplete, this.refreshComplete, this);
        this.parent.on(events.cancelBegin, this.cancelBeginEvent, this);
    };
    AdvancedFilter.prototype.removeEventListener = function () {
        if (this.parent.isDestroyed) {
            return;
        }
        this.parent.off(events.refreshComplete, this.refreshComplete);
        this.parent.off(events.cancelBegin, this.cancelBeginEvent);
    };
    AdvancedFilter.prototype.getAdvancedFilterSettings = function () {
        return this.parent.advancedFilterSettings || {};
    };
    AdvancedFilter.prototype.openDialog = function () {
        if (!this.parent.allowAdvancedFiltering) {
            return;
        }
        if (!this.dialog) {
            this.createDialog();
        }
        if (this.dialog && this.dialog.visible === false) {
            var eventArgs = {
                requestType: 'advancedFilterOpen',
                dialog: this.dialog,
                queryBuilder: this.queryBuilder
            };
            this.parent.trigger('advancedFilterOpen', eventArgs);
            this.dialog.show();
        }
    };
    AdvancedFilter.prototype.closeDialog = function () {
        if (this.dialog && this.dialog.visible) {
            this.saveCurrentValidRule();
            this.dialog.hide();
        }
    };
    /// Saves the current valid rule from the dialog's QueryBuilder before closing.
    AdvancedFilter.prototype.saveCurrentValidRule = function () {
        if (!this.queryBuilder) {
            return;
        }
        var currentDialogRule = this.queryBuilder.getRules();
        if (!this.hasValidFilterRule(currentDialogRule)) {
            this.filterRuleToRestore = null;
            return;
        }
        this.filterRuleToRestore = extend({}, {}, currentDialogRule, true);
    };
    /// Validates if a rule contains all required filter information (field, operator, and value).
    AdvancedFilter.prototype.hasValidFilterRule = function (rule) {
        var _this = this;
        if (!rule) {
            return false;
        }
        if (rule.rules && rule.rules.length > 0) {
            return rule.rules.some(function (child) { return _this.hasValidFilterRule(child); });
        }
        return !!(rule.field && rule.operator && (this.isValueLessOperator(rule.operator)
            || (rule.value !== undefined && rule.value !== null && rule.value !== '')));
    };
    /// Applies an advanced filter rule to the grid data. Triggers events and updates the query.
    AdvancedFilter.prototype.applyFilter = function (rule) {
        if (!rule || !rule.rules || rule.rules.length === 0 || this.isEmptyRule(rule)) {
            this.clearFilter();
            return;
        }
        if (this.dialog && this.dialog.visible) {
            var closeArgs = {
                requestType: 'advancedFilterClose',
                dialog: this.dialog,
                queryBuilder: this.queryBuilder,
                cancel: false
            };
            this.parent.trigger('advancedFilterClose', closeArgs);
            if (closeArgs.cancel) {
                return;
            }
            this.closeDialog();
        }
        this.hasEmptyState = false;
        this.parent.trigger(events.advancedFilterBegin, {
            requestType: 'filtering',
            type: events.advancedFilterBegin,
            cancel: false,
            rule: rule,
            dialog: this.dialog,
            queryBuilder: this.queryBuilder
        });
        var predicate = this.getPredicateFromRule(rule);
        if (!predicate) {
            return;
        }
        if (!this.baseQuery && this.parent.query) {
            this.baseQuery = this.parent.query.clone();
        }
        this.isAdvancedFilterApplied = true;
        this.filterRequestType = 'filtering';
        this.currentRule = extend({}, {}, rule, true);
        this.filterRuleToRestore = extend({}, {}, rule, true);
        if (this.baseQuery) {
            this.parent.query = this.baseQuery.clone().where(predicate);
        }
        else {
            this.parent.query = new Query().where(predicate);
        }
        this.setAdvancedFilterEnabled(true);
    };
    AdvancedFilter.prototype.refreshComplete = function (args) {
        if (this.isAdvancedFilterApplied && args.requestType === 'refresh') {
            this.isAdvancedFilterApplied = false;
            this.parent.trigger(events.advancedFilterComplete, {
                requestType: this.filterRequestType || 'filtering',
                type: events.advancedFilterComplete,
                cancel: false,
                rule: this.currentRule
            });
            this.filterRequestType = null;
        }
    };
    AdvancedFilter.prototype.cancelBeginEvent = function (args) {
        if (this.isAdvancedFilterApplied && args.requestType === 'refresh') {
            this.isAdvancedFilterApplied = false;
            this.filterRequestType = null;
        }
    };
    /// Checks if a rule or any of its child rules contain empty values.
    AdvancedFilter.prototype.isEmptyRule = function (rule) {
        var _this = this;
        if (!rule || !rule.rules || rule.rules.length === 0) {
            return true;
        }
        return rule.rules.every(function (child) {
            if (child.rules) {
                return _this.isEmptyRule(child);
            }
            if (_this.isValueLessOperator(child.operator)) {
                return false;
            }
            var value = child.value;
            return value === undefined || value === null || value === ''
                || (Array.isArray(value) && value.length === 0);
        });
    };
    /// Clears the advanced filter and restores the original query state.
    AdvancedFilter.prototype.clearFilter = function () {
        if (this.dialog && this.dialog.visible) {
            var closeArgs = {
                requestType: 'advancedFilterClose',
                dialog: this.dialog,
                queryBuilder: this.queryBuilder,
                cancel: false
            };
            this.parent.trigger('advancedFilterClose', closeArgs);
            if (closeArgs.cancel) {
                return;
            }
            this.closeDialog();
        }
        this.isAdvancedFilterApplied = true;
        this.filterRequestType = 'clear-filtering';
        this.parent.trigger(events.advancedFilterBegin, {
            requestType: 'clear-filtering',
            type: events.advancedFilterBegin,
            cancel: false,
            dialog: this.dialog,
            queryBuilder: this.queryBuilder
        });
        this.parent.query = this.baseQuery || new Query();
        this.currentRule = null;
        this.filterRuleToRestore = null;
        this.baseQuery = null;
        this.hasEmptyState = true;
        this.setAdvancedFilterEnabled(false);
    };
    /// Refreshes the QueryBuilder UI when grid columns change.
    AdvancedFilter.prototype.refresh = function () {
        if (!this.queryBuilder || this.queryBuilder.isDestroyed) {
            return;
        }
        var columns = this.mapColumns();
        var emptyRule = { condition: 'and', rules: [] };
        var persistedValidRule = this.hasValidFilterRule(this.filterRuleToRestore)
            ? this.filterRuleToRestore
            : (this.hasValidFilterRule(this.currentRule) ? this.currentRule : null);
        var rule = persistedValidRule || (!this.hasEmptyState ? { condition: 'and', rules: [] } : emptyRule);
        if (columns.length > 0) {
            var normalizedRule = this.normalizeRule(rule, columns)
                || (this.hasEmptyState ? emptyRule : { condition: 'and', rules: [] });
            this.queryBuilder.setRules(normalizedRule);
        }
        this.queryBuilder.refresh();
    };
    /// Gets the currently active filter rule applied to the grid.
    AdvancedFilter.prototype.getRule = function () {
        return this.currentRule;
    };
    /// Sets the filter rule programmatically and updates the QueryBuilder UI.
    AdvancedFilter.prototype.setRule = function (rule) {
        if (!rule) {
            this.currentRule = null;
            this.filterRuleToRestore = null;
            this.hasEmptyState = true;
            this.updateQueryBuilder({ condition: 'and', rules: [] });
            return;
        }
        this.currentRule = extend({}, {}, rule, true);
        this.filterRuleToRestore = extend({}, {}, rule, true);
        this.hasEmptyState = false;
        this.updateQueryBuilder(rule);
    };
    AdvancedFilter.prototype.updateQueryBuilder = function (rule) {
        if (this.queryBuilder && !this.queryBuilder.isDestroyed) {
            this.queryBuilder.setRules(rule);
            this.queryBuilder.refresh();
        }
    };
    AdvancedFilter.prototype.setAdvancedFilterEnabled = function (active) {
        var gridParent = this.parent;
        if (gridParent.toolbarModule) {
            gridParent.toolbarModule.enableAdvancedFilterButton(active);
        }
    };
    /// Maps grid columns to QueryBuilder compatible column definitions with appropriate types and operators.
    AdvancedFilter.prototype.mapColumns = function () {
        var _this = this;
        if (!this.parent.columns || this.parent.columns.length === 0) {
            return [];
        }
        var qbColumns = [];
        var includeHiddenColumns = this.getAdvancedFilterSettings().includeHiddenColumns === true;
        var processColumns = function (columns) {
            for (var _i = 0, columns_1 = columns; _i < columns_1.length; _i++) {
                var col = columns_1[_i];
                if (col.columns && col.columns.length) {
                    processColumns(col.columns);
                    continue;
                }
                if (col.allowFiltering === false || (!includeHiddenColumns && col.visible === false) || !col.field) {
                    continue;
                }
                var qbType = _this.mapGridTypeToQBType(col.type);
                var qbColumn = {
                    field: col.field,
                    label: col.headerText || col.field,
                    type: qbType,
                    operators: _this.getOperatorsForType(qbType).map(function (op) { return ({
                        key: _this.getOperatorLabel(op),
                        value: op
                    }); }),
                    dataSource: col.dataSource,
                    format: (col.type === 'date' || col.type === 'dateonly' || col.type === 'datetime') ? (col.format || 'yMd') : undefined
                };
                qbColumns.push(qbColumn);
            }
        };
        processColumns(this.parent.columns);
        return qbColumns;
    };
    /// Converts EJ2 Grid column type to QueryBuilder compatible type.
    AdvancedFilter.prototype.mapGridTypeToQBType = function (gridType) {
        if (!gridType) {
            return 'string';
        }
        var typeMap = {
            'string': 'string',
            'number': 'number',
            'date': 'date',
            'dateonly': 'date',
            'datetime': 'date',
            'boolean': 'boolean'
        };
        return typeMap[gridType.toLowerCase()] || 'string';
    };
    /// Returns the valid filter operators available for a specific data type.
    AdvancedFilter.prototype.getOperatorsForType = function (type) {
        var operatorsByType = {
            'number': ['equal', 'notequal', 'lessthan', 'lessthanorequal', 'greaterthan', 'greaterthanorequal', 'between', 'in', 'notin', 'isnull', 'isnotnull'],
            'date': ['equal', 'notequal', 'lessthan', 'lessthanorequal', 'greaterthan', 'greaterthanorequal', 'between'],
            'datetime': ['equal', 'notequal', 'lessthan', 'lessthanorequal', 'greaterthan', 'greaterthanorequal', 'between'],
            'boolean': ['equal', 'notequal'],
            'string': ['startswith', 'notstartswith', 'endswith', 'notendswith', 'contains', 'notcontains',
                'equal', 'notequal', 'in', 'notin', 'isempty', 'isnotempty', 'isnull', 'isnotnull']
        };
        return operatorsByType[type.toLowerCase()] || operatorsByType['string'];
    };
    AdvancedFilter.prototype.getOperatorLocaleKey = function (operator) {
        var localeKeys = [
            ['equal', 'Equal'], ['notequal', 'NotEqual'], ['contains', 'Contains'], ['notcontains', 'NotContains'],
            ['startswith', 'StartsWith'], ['notstartswith', 'NotStartsWith'], ['endswith', 'EndsWith'], ['notendswith', 'NotEndsWith'],
            ['in', 'In'], ['notin', 'NotIn'], ['isempty', 'IsEmpty'], ['isnotempty', 'IsNotEmpty'],
            ['isnull', 'IsNull'], ['isnotnull', 'NotNull'], ['lessthan', 'LessThan'], ['lessthanorequal', 'LessThanOrEqual'],
            ['greaterthan', 'GreaterThan'], ['greaterthanorequal', 'GreaterThanOrEqual'], ['between', 'Between']
        ];
        var localeKey = localeKeys.find(function (entry) { return entry[0] === operator; });
        return localeKey ? localeKey[1] : operator;
    };
    AdvancedFilter.prototype.getOperatorLabel = function (operator) {
        var label = this.l10n.getConstant(this.getOperatorLocaleKey(operator));
        return this.isValueLessOperator(operator) && label.indexOf('Is ') !== 0 ? 'Is ' + label : label;
    };
    AdvancedFilter.prototype.createDialog = function () {
        this.qbContainer = createElement('div', {
            id: this.parent.element.id + '_qbHost',
            className: 'e-advanced-filter-qb-host e-querybuilder',
            styles: 'width:100%; height:100%; display:block; padding:0; margin:0; border: none; box-shadow: none; overflow: auto; box-sizing:border-box;'
        });
        this.dialogContainer = document.createElement('div');
        this.dialogContainer.id = this.parent.element.id + '_advancedFilterDialog';
        this.dialogContainer.className = 'e-advanced-filter-dialog';
        if (document.body) {
            document.body.appendChild(this.dialogContainer);
        }
        else {
            this.parent.element.appendChild(this.dialogContainer);
        }
        var dialogModel = {
            header: this.l10n.getConstant('AdvancedFilter') || 'Advanced Filter',
            content: this.qbContainer,
            isModal: true,
            showCloseIcon: true,
            closeOnEscape: true,
            animationSettings: { effect: 'None' },
            width: 820,
            height: 480,
            buttons: this.getDialogButtons(),
            visible: false,
            target: document.body,
            beforeOpen: this.onDialogOpen.bind(this),
            open: this.onDialogOpened.bind(this),
            enableRtl: this.parent.enableRtl,
            locale: this.parent.locale
        };
        this.dialog = new Dialog(dialogModel);
        this.dialog.appendTo(this.dialogContainer);
        this.applyDialogSizeConstraints(this.dialog);
    };
    AdvancedFilter.prototype.applyDialogSizeConstraints = function (dialog) {
        var element = dialog.element;
        if (!element) {
            return;
        }
        element.style.minWidth = this.getDialogMinWidth() + 'px';
        element.style.minHeight = this.getDialogMinHeight() + 'px';
    };
    AdvancedFilter.prototype.getDialogMinHeight = function () {
        return 360;
    };
    AdvancedFilter.prototype.getDialogMinWidth = function () {
        return 520;
    };
    AdvancedFilter.prototype.getDialogButtons = function () {
        return [
            {
                click: this.onCancelClick.bind(this),
                buttonModel: {
                    content: this.l10n.getConstant('CancelAdvancedFilter') || this.l10n.getConstant('Cancel') || 'Cancel'
                }
            },
            {
                click: this.onApplyClick.bind(this),
                buttonModel: {
                    content: this.l10n.getConstant('ApplyAdvancedFilter') || this.l10n.getConstant('Apply') || 'Apply',
                    isPrimary: true
                }
            }
        ];
    };
    /// Converts a filter rule to a Data Query predicate for applying grid filters.
    AdvancedFilter.prototype.getPredicateFromRule = function (rule) {
        var qbColumns = this.mapColumns();
        if (qbColumns.length === 0) {
            return null;
        }
        var normalizedRule = this.normalizeRule(rule, qbColumns) || rule;
        var tempContainer = createElement('div');
        document.body.appendChild(tempContainer);
        var tempQueryBuilder = new QueryBuilder({
            columns: qbColumns,
            rule: normalizedRule
        });
        tempQueryBuilder.appendTo(tempContainer);
        try {
            return tempQueryBuilder.getPredicate(normalizedRule);
        }
        finally {
            tempQueryBuilder.destroy();
            remove(tempContainer);
        }
    };
    /// Creates and initializes the QueryBuilder component within the dialog.
    AdvancedFilter.prototype.createQueryBuilder = function () {
        if (this.queryBuilder || !this.qbContainer) {
            return;
        }
        var qbColumns = this.mapColumns();
        if (qbColumns.length === 0) {
            return;
        }
        Object.assign(this.qbContainer.style, { width: '100%', padding: '0', margin: '0' });
        var advancedFilterSettings = this.getAdvancedFilterSettings();
        var userQueryBuilderSettings = (advancedFilterSettings && advancedFilterSettings.queryBuilderSettings) || {};
        var qbConfig = extend({}, {
            columns: qbColumns,
            displayMode: 'Horizontal',
            width: '100%',
            locale: this.parent.locale,
            enableRtl: this.parent.enableRtl,
            dataSource: this.parent.dataSource,
            valueModel: {
                multiSelectModel: {
                    mode: 'CheckBox',
                    allowFiltering: true,
                    showSelectAll: true
                }
            }
        }, userQueryBuilderSettings, true);
        this.queryBuilder = new QueryBuilder(qbConfig);
        this.queryBuilder.appendTo(this.qbContainer);
    };
    /// Selects the best rule to restore to the QueryBuilder: current > saved > configured.
    AdvancedFilter.prototype.getRuleToRestore = function (configuredRule) {
        if (this.hasEmptyState) {
            return { condition: 'and', rules: [] };
        }
        var rule = (this.hasValidFilterRule(this.currentRule) && this.currentRule)
            || (this.hasValidFilterRule(this.filterRuleToRestore) && this.filterRuleToRestore)
            || configuredRule;
        return rule ? extend({}, {}, rule, true) : null;
    };
    /// Validates and normalizes a rule by removing non-existent columns and resolving operators.
    AdvancedFilter.prototype.normalizeRule = function (rule, columns) {
        var _this = this;
        if (!rule) {
            return null;
        }
        var resolveRule = function (currentRule) {
            var clonedRule = extend({}, {}, currentRule, true);
            if (clonedRule.rules && clonedRule.rules.length > 0) {
                var validChildren = [];
                for (var _i = 0, _a = clonedRule.rules; _i < _a.length; _i++) {
                    var childRule = _a[_i];
                    var processedChild = resolveRule(childRule);
                    if (processedChild) {
                        validChildren.push(processedChild);
                    }
                }
                return validChildren.length > 0 ? {
                    condition: clonedRule.condition || 'and',
                    rules: validChildren,
                    not: clonedRule.not || false
                } : null;
            }
            if (!clonedRule.field) {
                return null;
            }
            var qbColumn = columns.find(function (col) { return col.field === clonedRule.field; });
            if (!qbColumn) {
                return null;
            }
            var operatorList = (qbColumn.operators || []).map(function (op) {
                return typeof op === 'string' ? op : (op.value || op.key || '');
            }).filter(function (op) { return !!op; });
            var resolvedOperator = operatorList.indexOf(clonedRule.operator) >= 0
                ? clonedRule.operator
                : (operatorList[0] || 'equal');
            return {
                field: qbColumn.field,
                label: clonedRule.label || qbColumn.label,
                type: qbColumn.type,
                operator: resolvedOperator,
                value: clonedRule.value !== undefined && clonedRule.value !== null
                    ? clonedRule.value : _this.isValueLessOperator(resolvedOperator) ? undefined : ''
            };
        };
        var normalizedRule = resolveRule(rule);
        if (normalizedRule && normalizedRule.rules && normalizedRule.rules.length > 0) {
            return normalizedRule;
        }
        return normalizedRule && normalizedRule.field ? { condition: 'and', rules: [normalizedRule] } : null;
    };
    AdvancedFilter.prototype.isValueLessOperator = function (operator) {
        return operator === 'isnull' || operator === 'isnotnull' || operator === 'isempty' || operator === 'isnotempty';
    };
    /// Restores the filter rule to the QueryBuilder display when the dialog opens.
    AdvancedFilter.prototype.restoreQueryBuilderRule = function () {
        if (!this.queryBuilder || this.queryBuilder.isDestroyed) {
            return;
        }
        var emptyRule = { condition: 'and', rules: [] };
        if (this.hasEmptyState) {
            this.queryBuilder.setRules(emptyRule);
            return;
        }
        var columns = this.mapColumns();
        var advancedFilterSettings = this.getAdvancedFilterSettings();
        var configuredRuleValue = advancedFilterSettings && advancedFilterSettings.queryBuilderSettings
            ? advancedFilterSettings.queryBuilderSettings.rule
            : undefined;
        var configuredRule = this.normalizeRule(configuredRuleValue, columns);
        var ruleToRestore = this.getRuleToRestore(configuredRule);
        if (ruleToRestore && this.hasValidFilterRule(ruleToRestore)) {
            this.queryBuilder.setRules(ruleToRestore);
            this.queryBuilder.refresh();
            return;
        }
        this.filterRuleToRestore = null;
        this.currentRule = null;
        this.queryBuilder.setRules(emptyRule);
    };
    AdvancedFilter.prototype.onDialogOpen = function () {
        if (!this.qbContainer) {
            return;
        }
        if (this.queryBuilder && !this.queryBuilder.isDestroyed) {
            this.queryBuilder.destroy();
            this.queryBuilder = null;
        }
        this.qbContainer.innerHTML = '';
        this.createQueryBuilder();
        this.restoreQueryBuilderRule();
    };
    AdvancedFilter.prototype.onDialogOpened = function () {
        if (this.queryBuilder && !this.queryBuilder.isDestroyed && this.qbContainer) {
            var fieldSelect = this.qbContainer.querySelector('.e-field-select, select[id$="_field"], input.e-qb-field');
            if (fieldSelect) {
                fieldSelect.focus();
            }
        }
    };
    AdvancedFilter.prototype.onApplyClick = function () {
        if (!this.queryBuilder) {
            return;
        }
        var rule = this.queryBuilder.getRules();
        if (rule) {
            this.applyFilter(rule);
        }
    };
    AdvancedFilter.prototype.onCancelClick = function () {
        this.closeDialog();
    };
    return AdvancedFilter;
}());
export { AdvancedFilter };
